import type { ActivityIndexingRow } from '~~/shared/activity/activityFeed'
import type { QuotaBlockInfo } from '~~/shared/usage/quotaBlock'
import { deriveIndexState, type IndexState as ServerIndexState } from '~~/shared/indexing/indexState'
import type { RequestFailureKind } from '~/utils/requestFailure'

/**
 * The client's job states: the server's `IndexState` (`shared/indexing/indexState.ts`) plus the two
 * only a browser tab can know — `uploading` (the bytes are still on the wire, no server row yet) and
 * `duplicate` (the org already held these bytes, so nothing was created, §5).
 *
 * `quota_blocked` is its OWN state, not a flavour of `failed` (§10.7). A failure is the document's
 * fault and a Retry might fix it; a quota block is neither — the document is intact and a Retry is
 * guaranteed to fail again until the allowance changes.
 */
export type IndexState = ServerIndexState | 'uploading' | 'duplicate'

/**
 * Phase within a run, as docproc reports it (`POST /api/activity`'s own type). Two, not four:
 * embeddings and entity extraction happen concurrently, so listing them separately would invent an
 * order the pipeline doesn't have.
 */
export type IndexStage = NonNullable<ActivityIndexingRow['stage']>

export type IndexSource = 'chat' | 'library' | 'dms' | 'addin' | 'workflow'

/** Why an UPLOAD failed before the server had a row — the request's kind, or the user's own cancel. */
export type UploadFailureKind = RequestFailureKind | 'cancelled'

export interface IndexJob {
  /**
   * Stable for the job's whole life, INCLUDING the upload that precedes the server row (MAR-113).
   *
   * The job used to be keyed by `itemId`, which is why an in-flight upload could not be one: it has
   * no server id until the POST resolves. Minting the identity client-side means the row the user
   * sees the instant they drop a file is the same row that later becomes an indexing job — no ghost
   * to reconcile, no ghost to leak, and no id change to make the Activity Center row blink.
   */
  id: string
  /** The server row, once it exists. `null` for an upload still on the wire or one that was refused. */
  itemId: string | null
  name: string
  fileName: string | null
  /** Destination folder. Lets a folder view render its own in-flight rows without holding any state. */
  path: string
  source: IndexSource
  state: IndexState
  errorMessage: string | null
  /**
   * Set with `errorMessage` when an upload fails client-side. The message is for the Activity Center;
   * a surface that must speak the user's language (onboarding, MAR-933 F4) translates this instead,
   * since the message may be the browser's own English ("Failed to fetch").
   */
  failureKind?: UploadFailureKind
  /** Populated for `quota_blocked` rows — the numbers the tray needs to say WHY (§10.7). */
  quota?: QuotaBlockInfo | null
  /** Set when this upload was refused because the org already held these bytes (§5). */
  duplicate?: boolean
  /** Still `working`, but quiet long enough that the worker probably died — offer a retry. */
  stale?: boolean
  /** Which phase the run is in, when docproc reported one (§6). Undefined = working, no detail. */
  stage?: IndexStage | null
  startedAt: number
  settledAt?: number
  /** Acknowledged server-side; present only while "mostrar descartados" is on. */
  dismissedAt?: string | null
}

type OptionalIndexFacts = 'quota' | 'stale' | 'stage' | 'dismissedAt'
/**
 * One indexing row as `POST /api/activity` reports it — the endpoint's own type (MAR-948), with the
 * four secondary facts optional because `applyRows` treats an absent one as "nothing to report".
 */
export type IndexRow = Omit<ActivityIndexingRow, OptionalIndexFacts> & Partial<Pick<ActivityIndexingRow, OptionalIndexFacts>>

/**
 * Map a raw `items.status` to a job state, for callers that only have the legacy column.
 *
 * Delegates to the ONE shared mapping (`shared/indexing/indexState.ts`) rather than keeping a copy:
 * the copy that lived here had no `quota_blocked` arm and reported such a row as `ready` (MAR-957).
 */
export function stateFromStatus(status: string | null | undefined): IndexState {
  return deriveIndexState(null, status ?? null, false)
}
